import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import { styled } from '@mui/system'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { storageGet, storageSet } from '../../../../utils/storage'
import { useDispatch } from 'react-redux'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../utils/sorting'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import { UspConfigurationCarExpenseTypeView } from '../../../../models'
import { useCarExpenseDeleteMutation } from '../../../../services'
import { enqueueNotification } from '../../../../redux/slices/notificationsSlice'
import { CustomCircular } from '../../../../ui'
import CustomPopover from '../../../../components/custom-popover/custom-popover'
import Iconify from '../../../../components/iconify'
import { usePopover } from '../../../../components/custom-popover'
import Label from '../../../../components/label/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'loading', 'tableHead', 'tableCellName', 'inactiveItem'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))

interface Props {
  data: UspConfigurationCarExpenseTypeView[]
  loading: boolean
  setData: Dispatch<SetStateAction<UspConfigurationCarExpenseTypeView[]>>
  setOpenAddEditModal: Dispatch<SetStateAction<boolean>>
  setType: Dispatch<SetStateAction<string>>
  setRowData: Dispatch<SetStateAction<UspConfigurationCarExpenseTypeView>>
}

const ExpenseTypeTable = ({ data, loading, setData, setOpenAddEditModal, setRowData, setType }: Props) => {
  const [deleteCarExpense, { isSuccess: deleteIsSuccess }] = useCarExpenseDeleteMutation()
  const initialOrder = (storageGet('settings_expenses_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('settings_expenses_order_by') || 'ExpenseType') as keyof UspConfigurationCarExpenseTypeView
  const [order, setOrder] = React.useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof UspConfigurationCarExpenseTypeView>(initialOrderBy)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = React.useState<{ carExpenseId: string } | undefined>(undefined)
  const dispatch = useDispatch()
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspConfigurationCarExpenseTypeView) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('settings_expenses_order', isAsc ? 'desc' : 'asc')
    storageSet('settings_expenses_order_by', property)
  }

  const createSortHandler = (property: keyof UspConfigurationCarExpenseTypeView) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.carExpenseId) {
      await deleteCarExpense(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  const handleOpen = () => {
    setType('edit')
    setOpenAddEditModal(true)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.carExpenseId) {
      if (deleteIsSuccess) {
        let updatedData: UspConfigurationCarExpenseTypeView[]
        updatedData = data.map((item) => (item.ID?.toString() === deleteConfirmDialog.carExpenseId ? { ...item, IsActive: false } : item))
        setData(updatedData)
        dispatch(enqueueNotification({ message: 'Success' }))
      }
    }
  }, [deleteIsSuccess])

  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }} className={classes.root}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'ExpenseType' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'ExpenseType'}
                  direction={orderBy === 'ExpenseType' ? order : 'asc'}
                  onClick={createSortHandler('ExpenseType')}
                >
                  Name
                  {orderBy === 'ExpenseType' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsIncludeInPurchasePrice' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsIncludeInPurchasePrice'}
                  direction={orderBy === 'IsIncludeInPurchasePrice' ? order : 'asc'}
                  onClick={createSortHandler('IsIncludeInPurchasePrice')}
                >
                  Include In Purchase Price
                  {orderBy === 'IsIncludeInPurchasePrice' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsAvailableForUnsoldInventory' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsAvailableForUnsoldInventory'}
                  direction={orderBy === 'IsAvailableForUnsoldInventory' ? order : 'asc'}
                  onClick={createSortHandler('IsAvailableForUnsoldInventory')}
                >
                  Unsold Inventory
                  {orderBy === 'IsAvailableForUnsoldInventory' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsAvailableForSoldInventory' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsAvailableForSoldInventory'}
                  direction={orderBy === 'IsAvailableForSoldInventory' ? order : 'asc'}
                  onClick={createSortHandler('IsAvailableForSoldInventory')}
                >
                  Sold Inventory
                  {orderBy === 'IsAvailableForSoldInventory' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsSystem' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsSystem'}
                  direction={orderBy === 'IsSystem' ? order : 'asc'}
                  onClick={createSortHandler('IsSystem')}
                >
                  System
                  {orderBy === 'IsSystem' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), data as any)?.map((item) => (
              <TableRow key={item.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell
                  className={classes.tableCellName}
                  onClick={() => {
                    if (!item?.IsSystem) {
                      handleOpen()
                      setRowData(item as any)
                    }
                  }}
                >
                  {item.ExpenseType}
                </TableCell>
                <TableCell>{item?.IsIncludeInPurchasePrice ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsAvailableForUnsoldInventory ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsAvailableForSoldInventory ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsSystem ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                </TableCell>
                <TableCell align={'right'}>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setSelected(item)
                      popover.onOpen(event)
                    }}
                  >
                    <Iconify icon='eva:more-vertical-fill' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomCircular open={loading} />
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Expense will be removed'}
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          disabled={selected?.IsSystem ? true : false}
          onClick={(e) => {
            handleOpen()
            setRowData(selected as any)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          disabled={selected?.IsSystem || !selected?.IsActive ? true : false}
          onClick={() => {
            setDeleteConfirmDialog({ carExpenseId: selected.ID?.toString() as string })
            popover.onClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default ExpenseTypeTable
